import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import {SERVICE_CASE_PDF_COLUMNS} from '../constants/SERVICE_CASE_PDF_COLUMNS';

const EMPTY_VALUE = '-';
const labelSchema = z.object({label: z.string().nullable()});
const textCellSchema = z.object({value: z.string().nullable()});
const labelCellSchema = z.object({value: labelSchema.nullable()});
const markersCellSchema = z.object({value: z.array(labelSchema.nullable()).nullable()});

/** Export the selected snapshot, including fields hidden in the user's grid preset. */
export function createServiceCasePdfData(
  context: GridPdfExportContext,
  format: {dateTime: (value: string) => string}
) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }
  const titles = new Map(context.columns.map((column) => [column.key, column.title]));
  return context.rows.map((row) => {
    const field = (key: (typeof SERVICE_CASE_PDF_COLUMNS)[number]) => {
      try {
        const label = titles.get(key);
        if (isNil(label)) {
          throw new Error(`Missing service-case PDF column: ${key}`);
        }
        const cell = row.cells[key];
        const value = match(key)
          .with('markers', () =>
            markersCellSchema
              .parse(cell)
              .value?.map((marker) => marker?.label?.trim() ?? '')
              .filter((text) => text.length > 0)
              .join('; ')
          )
          .with('scheduledCheckinDate', () => {
            const date = textCellSchema.parse(cell).value;
            return isNil(date) || date.length === 0 ? date : format.dateTime(date);
          })
          .with(
            'caseNumber',
            'vehicleRegistrationPlate',
            'vehicleVin',
            'serviceCaseJob',
            () => textCellSchema.parse(cell).value
          )
          .otherwise(() => labelCellSchema.parse(cell).value?.label);
        return {key, label, value: value || EMPTY_VALUE};
      } catch (cause) {
        throw new PdfExportError(
          'module.reportDocument.labels.dataUnavailable',
          false,
          'entity.integration.labels.pleaseRefresh',
          {cause}
        );
      }
    };

    return {
      id: row.id,
      caseNumber: field('caseNumber'),
      branch: field('branch'),
      state: field('state'),
      scheduledCheckinDate: field('scheduledCheckinDate'),
      vehicle: field('vehicle'),
      vehicleRegistrationPlate: field('vehicleRegistrationPlate'),
      vehicleVin: field('vehicleVin'),
      customerName: field('customerName'),
      customerVehicleUser: field('customerVehicleUser'),
      markers: field('markers'),
      serviceCaseJob: field('serviceCaseJob'),
    } satisfies Record<
      (typeof SERVICE_CASE_PDF_COLUMNS)[number],
      {key: string; label: string; value: string}
    > & {id: string};
  });
}
